import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="address-autocomplete"
export default class extends Controller {
  static targets = [
    "searchTerm",
    "formattedAddress",
    "line1", "line2", "line3", "line4",
    "townOrCity", "county", "postcode", "country",
    "district", "locality", "thoroughfare",
    "buildingName", "subBuildingName", "subBuildingNumber", "buildingNumber",
    "latitude", "longitude", "residential"
  ]

  static values = { sessionToken: String }

  connect() {
  }

  // Handle selection event from autocomplete controller
  async handleSelect(event) {
    const { id } = event.detail

    if (!id) {
      console.error("Address autocomplete: No ID provided in selection")
      return
    }

    try {
      const address = await this.fetchAddress(id)
      if (address) {
        this.populateFields(address)
      }
    } catch (error) {
      console.error("Address autocomplete error:", error)
      // Optionally show error to user
    }
  }

  // Fetch full address details from backend
  async fetchAddress(id) {
    const sessionToken = this.hasSessionTokenValue ? this.sessionTokenValue : ""
    const url = `/addresses/search/${encodeURIComponent(id)}${sessionToken ? `?session_token=${encodeURIComponent(sessionToken)}` : ""}`
    const response = await fetch(url, {
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  // Populate all form fields with address data
  populateFields(address) {
    // Store formatted_address as JSON
    if (this.hasFormattedAddressTarget && address.formatted_address) {
      this.formattedAddressTarget.value = JSON.stringify(address.formatted_address)
    }

    // Every field comes from its own key. formatted_address is display lines
    // (invoice PDFs, local-cache suggestion titles), not a field layout —
    // reading the lines out of it by position put a picked postcode in Line 1
    // and the country in Town/City (TASK-203).
    this.setFieldValue("line1", address.line_1 || "")
    this.setFieldValue("line2", address.line_2 || "")
    this.setFieldValue("line3", address.line_3 || "")
    this.setFieldValue("townOrCity", address.town_or_city || "")
    this.setFieldValue("county", address.county || "")

    // Additional fields
    this.setFieldValue("line4", address.line_4 || "")
    this.setFieldValue("postcode", address.postcode || "")
    this.setFieldValue("country", address.country || "")
    this.setFieldValue("district", address.district || "")
    this.setFieldValue("locality", address.locality || "")
    this.setFieldValue("thoroughfare", address.thoroughfare || "")
    this.setFieldValue("buildingName", address.building_name || "")
    this.setFieldValue("subBuildingName", address.sub_building_name || "")
    this.setFieldValue("subBuildingNumber", address.sub_building_number || "")
    this.setFieldValue("buildingNumber", address.building_number || "")
    this.setFieldValue("latitude", address.latitude || "")
    this.setFieldValue("longitude", address.longitude || "")

    // Residential checkbox
    if (this.hasResidentialTarget) {
      this.residentialTarget.checked = address.residential === true
    }

    // Trigger change events for any validation or other listeners
    this.triggerChangeEvents()
  }

  // Set field value if target exists
  setFieldValue(targetName, value) {
    const targetKey = `${targetName}Target`
    if (this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`] && this[targetKey]) {
      this[targetKey].value = value
    }
  }

  // Trigger change events on all populated fields
  triggerChangeEvents() {
    const allTargets = [
      "line1", "line2", "line3", "line4",
      "townOrCity", "county", "postcode", "country",
      "district", "locality", "thoroughfare",
      "buildingName", "subBuildingName", "subBuildingNumber", "buildingNumber",
      "latitude", "longitude"
    ]

    allTargets.forEach(targetName => {
      const targetKey = `${targetName}Target`
      const hasTargetKey = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      
      if (this[hasTargetKey] && this[targetKey]) {
        const event = new Event("change", { bubbles: true })
        this[targetKey].dispatchEvent(event)
      }
    })

    // Trigger change on residential checkbox
    if (this.hasResidentialTarget) {
      const event = new Event("change", { bubbles: true })
      this.residentialTarget.dispatchEvent(event)
    }
  }
}

